import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { formatViews } from '@/components/feature/NewsCard';
import { gossipItems, channels, channelLabel, type ChannelKey } from '@/mocks/gossip';
import { useSeo } from '@/hooks/useSeo';
import HotRank from '@/pages/home/components/HotRank';

function NewsRow({ slug }: { slug: string }) {
  const item = gossipItems.find((i) => i.slug === slug);
  if (!item) return null;
  return (
    <Link
      to={`/news/${item.slug}`}
      className="group grid gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:grid-cols-[220px_1fr]"
    >
      <div className="relative aspect-[10/7] w-full overflow-hidden rounded-lg bg-background-200 sm:aspect-[16/11]">
        <img src={item.cover} alt={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {item.hotTag && (
          <span className="absolute left-2 top-2 rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-black text-background-50">{item.hotTag}</span>
        )}
      </div>
      <div className="flex flex-col justify-center px-1 sm:px-2">
        <p className="text-xs font-black tracking-wider text-primary-600">{channelLabel(item.channel)}</p>
        <h3 className="mt-1.5 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-700 md:text-lg">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{item.summary}</p>
        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
          <span>{item.date}</span>
          <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(item.views)}</span>
          <span className="flex items-center gap-1"><i className="ri-time-line"></i>{item.readTime} 分钟</span>
        </div>
      </div>
    </Link>
  );
}

export default function Daily() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const g = params.get('g') || 'all';
  const [kw, setKw] = useState(q);

  const list = useMemo(() => {
    const sorted = [...gossipItems].sort((a, b) => (a.date < b.date ? 1 : -1));
    const byChannel = g === 'all' ? sorted : sorted.filter((i) => i.channel === g);
    if (!q.trim()) return byChannel;
    const s = q.trim().toLowerCase();
    return byChannel.filter(
      (i) =>
        i.title.toLowerCase().includes(s) ||
        i.summary.toLowerCase().includes(s) ||
        i.tags.some((t) => t.toLowerCase().includes(s)) ||
        channelLabel(i.channel).includes(s)
    );
  }, [q, g]);

  useSeo({
    title: '每日合集 | 吃瓜日报今日最新瓜料速览',
    description: '吃瓜日报每日合集页，按日期与频道浏览当日最新瓜料与热门事件，支持关键词搜索，快速锁定今天值得吃的新瓜。',
    keywords: '吃瓜日报每日合集,吃瓜日报更新,吃瓜日报最新,今日瓜料',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '吃瓜日报每日合集',
        description: '吃瓜日报每日更新，汇集当日最新瓜料与热门事件',
        isPartOf: { '@type': 'WebSite', name: '吃瓜日报', url: 'https://chiguadaily.example' },
      },
    ],
  });

  const setChannel = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'all') next.delete('g');
    else next.set('g', key);
    setParams(next, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (kw.trim()) next.set('q', kw.trim());
    else next.delete('q');
    setParams(next, { replace: true });
  };

  const groupLabel = g === 'all' ? '全部频道' : channelLabel(g as ChannelKey);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
            <nav className="text-xs text-foreground-400" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-600">每日合集</span>
            </nav>
            <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                  每日合集 · {groupLabel}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
                  最新更新以合集形式整理，支持按频道与关键词筛选。今日新瓜、实时进展都在这里，一次逛完不遗漏。
                </p>
              </div>
              <form onSubmit={submitSearch} className="flex h-12 w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 focus-within:border-primary-400 lg:w-80">
                <i className="ri-search-line text-base text-foreground-400"></i>
                <input
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  placeholder="输入关键词搜瓜"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                />
                <button type="submit" className="grid h-8 shrink-0 items-center rounded-full bg-primary-500 px-4 text-xs font-bold text-background-50 whitespace-nowrap hover:bg-primary-600">
                  搜索
                </button>
              </form>
            </div>
          </div>
        </section>

        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-6 flex flex-wrap items-center gap-2">
              {[{ key: 'all', label: '全部' } as const, ...channels.map((c) => ({ key: c.key, label: c.label }))].map((c) => (
                <button
                  key={c.key}
                  onClick={() => setChannel(c.key)}
                  className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                    g === c.key ? 'bg-primary-500 font-bold text-background-50' : 'border border-background-200 bg-background-50 text-foreground-600 hover:text-primary-700'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {q && (
              <p className="mb-4 text-sm text-foreground-600">
                关键词 <strong className="text-primary-700">「{q}」</strong> 共找到
                <strong className="text-primary-700"> {list.length} </strong> 条结果
                <button onClick={() => { const n = new URLSearchParams(params); n.delete('q'); setParams(n); setKw(''); }} className="ml-2 text-xs text-accent-600 hover:underline">
                  清除
                </button>
              </p>
            )}

            {list.length === 0 ? (
              <div className="rounded-xl border border-dashed border-background-300 bg-background-50 p-10 text-center">
                <i className="ri-emotion-sad-line text-4xl text-background-300"></i>
                <p className="mt-3 text-sm text-foreground-500">这个关键词暂时还没挖到新瓜，换个词试试？</p>
              </div>
            ) : (
              <div className="space-y-4">
                {list.map((it) => (
                  <NewsRow key={it.slug} slug={it.slug} />
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
            <HotRank />
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-black text-foreground-900">频道导航</h2>
              <ul className="mt-3 space-y-1">
                {channels.map((c) => (
                  <li key={c.key}>
                    <Link to={`/channel/${c.key}`} className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700">
                      <span className="flex items-center gap-2">
                        <i className={`${c.icon} text-primary-600`}></i>
                        {c.label}
                      </span>
                      <i className="ri-arrow-right-line text-foreground-300"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}